import { IconName } from '@fortawesome/fontawesome-svg-core';

import { getFileExtension } from '../../react/utils/path';

/**
 * Returns a human readable string for specified file size.
 * @param size Size in bytes.
 */
export function toHumanReadableFileSize(size: number): string {
  const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  if (size === 0) {
    return '0 KB';
  }

  let sizeIndex = Math.floor(Math.log(size) / Math.log(1024));
  let prefixSize = parseFloat((size / Math.pow(1024, sizeIndex)).toFixed(2));

  // Stop at TB
  if (sizeIndex > sizes.length - 1) {
    prefixSize *= Math.pow(1024, sizes.length - sizeIndex + 1);
    sizeIndex = sizes.length - 1;
  }

  return `${prefixSize} ${sizes[sizeIndex]}`;
}

export const fontAwesomeIconColorMap = new Map<IconName, string>([
  ['file-word', '#2780b3'],
  ['file-excel', '#27b345'],
  ['file-powerpoint', '#b32727'],
  ['file-archive', '#cece0e'],
  ['file-code', '#b327a7'],
  ['file-vector', '#b327a7'],
  ['file-export', '#1dbb28'],
  ['file-image', '#27b3ab'],
  ['file-pdf', '#b32727'],
]);

/**
 * Returns a FontAwesomeIcon 'IconName' and color for the extension in the specified file name.
 * @param filename Name of file.
 */
export function filenameToFontAwesomeIcon(filename: string): { icon: IconName; color: string } {
  const icons = new Map<string, IconName>([
    // Word
    ['.doc', 'file-word'],
    ['.docx', 'file-word'],

    // Power Point
    ['.ppt', 'file-powerpoint'],
    ['.pptx', 'file-powerpoint'],

    // Excel
    ['.xls', 'file-excel'],
    ['.xlsx', 'file-excel'],

    // Compression
    ['.7z', 'file-archive'],
    ['.rar', 'file-archive'],
    ['.zip', 'file-archive'],

    // CAD
    ['.dwg', 'file-code'],
    ['.dxf', 'file-code'],

    // Polygon
    ['.kml', 'file-vector'],
    ['.shp', 'file-vector'],
    ['.shap', 'file-vector'],
    ['.json', 'file-vector'],
    ['.geojson', 'file-vector'],

    // Point cloud
    ['.laz', 'file-export'],
    ['.las', 'file-export'],
    ['.pts', 'file-export'],

    // Images
    ['.gif', 'file-image'],
    ['.jpg', 'file-image'],
    ['.jpeg', 'file-image'],
    ['.png', 'file-image'],

    // Other
    ['.csv', 'file-csv'],
    ['.html', 'file-code'],
    ['.pdf', 'file-pdf'],
    ['.txt', 'file-alt'],
    ['.xml', 'file-code'],
  ]);

  // Color only specific icons.
  const colors = fontAwesomeIconColorMap;

  const extension = getFileExtension(filename).toLowerCase();
  const icon = icons.get(extension ?? '') ?? 'file';
  const color = colors.get(icon) ?? '#b6bdc0';

  return {
    icon,
    color,
  };
}
